import { Chip, Typography } from '@mui/material';
import moment from 'moment';
import {
	AutocompleteArrayInput,
	AutocompleteInput,
	ChipField,
	Datagrid,
	DateField,
	Filter,
	FunctionField,
	ReferenceArrayField,
	ReferenceArrayInput,
	ReferenceField,
	ReferenceInput,
	SingleFieldList,
	TextField,
	useListContext,
	usePermissions,
} from 'react-admin';
import { BetweenDatesTimesInput } from '../../components/BetweenDatesInput';
import Permission from '../../components/Permission';
import ReportList from '../../components/ReportList';
import RequiredChain from '../../components/RequiredChain';
import { PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import { GiftProductType, GiftRuleType, GiftValueType } from '../../models';
import { filterToQuery } from '../../utils';

const titleSx = {
	margin: '10px 0 10px 0',
	textAlign: 'center',
	fontWeight: 'bold',
};

const formatBRL = (value: any): string =>
	Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatNumber = (value: any): string => Number(value || 0).toLocaleString('pt-BR');

const formatLiters = (value: any): string => `${formatNumber(value)} L`;

const translateProductType = (type: GiftProductType): string => {
	switch (type) {
		case GiftProductType.Virtual:
			return 'Brinde virtual';
		case GiftProductType.Existing:
			return 'Produto do catálogo';
		case GiftProductType.FuelDiscount:
			return 'Desconto de combustível';
		case GiftProductType.PredictionTicket:
			return 'Recompensa de Palpite';
		case GiftProductType.DrawingNumber:
			return 'Número da Sorte';
		case GiftProductType.SpinTicket:
			return 'Giro na Roleta';
		default:
			return '-';
	}
};

const formatRuleValue = (value: any, valueType: GiftValueType): string => {
	if (!value) return '';
	const numericValue = Number(value);
	switch (valueType) {
		case GiftValueType.cents:
			return ` - R$ ${numericValue.toFixed(2)}`;
		case GiftValueType.volume:
			return ` - ${numericValue.toFixed(2)} L`;
		default:
			return ` - ${numericValue.toFixed(2)} L`;
	}
};

type ChipColor = 'default' | 'error' | 'info' | 'success' | 'warning';

const statusChips: Record<string, { label: string; color: ChipColor }> = {
	pending: { label: 'Pendente', color: 'warning' },
	redeemed: { label: 'Utilizado', color: 'success' },
	refunded: { label: 'Estornado', color: 'error' },
	expired: { label: 'Expirado', color: 'default' },
	guess_pending: { label: 'Palpite pendente', color: 'warning' },
	guess_made: { label: 'Palpite realizado', color: 'info' },
	guess_expired: { label: 'Palpite expirado', color: 'default' },
	winner: { label: 'Palpite vencedor', color: 'success' },
	drawing_pending: { label: 'Aguardando sorteio', color: 'warning' },
	drawing_done: { label: 'Não sorteado', color: 'default' },
	spin_available: { label: 'Giro pendente', color: 'warning' },
	spin_used: { label: 'Giro realizado', color: 'info' },
	spin_expired: { label: 'Giro expirado', color: 'default' },
};

// O status 'winner' é compartilhado pelo palpite e pelo número da sorte — o `kind`
// da linha do detalhe (coupon/discount/guess/drawing) desambigua o rótulo.
const statusChipsByKind: Record<string, Record<string, { label: string; color: ChipColor }>> = {
	drawing: {
		winner: { label: 'Número sorteado', color: 'success' },
	},
	// O giro reaproveita 'winner' para dizer que a roda parou num prêmio.
	spin: {
		winner: { label: 'Prêmio entregue', color: 'success' },
		pending: { label: 'Giro pendente', color: 'warning' },
		redeemed: { label: 'Giro realizado', color: 'info' },
		expired: { label: 'Giro expirado', color: 'default' },
	},
};

const getStatusChip = (status: string | undefined, kind: string | undefined) =>
	statusChipsByKind[kind || '']?.[status || '']
	|| statusChips[status || '']
	|| { label: '-', color: 'default' as ChipColor };

const translateStatus = (status: string | undefined, kind?: string): string =>
	getStatusChip(status, kind).label;

const StatusChip = ({ record }: { record: any }) => {
	const chip = getStatusChip(record?.status, record?.kind);
	return <Chip label={chip.label} size="small" color={chip.color} variant="outlined" />;
};

const summaryRows: { key: string; label: string; format?: (value: any) => string }[] = [
	{ key: 'uniqueCustomers', label: 'Clientes contemplados' },
	{ key: 'newCustomers', label: 'Clientes novos' },
	{ key: 'oldCustomers', label: 'Clientes antigos' },
	{ key: 'totalAchievements', label: 'Conquistas' },
	{ key: 'totalRewardsIssued', label: 'Recompensas emitidas' },
	{ key: 'totalRewardsUsed', label: 'Recompensas utilizadas' },
	{ key: 'totalRewardsPending', label: 'Recompensas pendentes' },
	{ key: 'totalRewardsExpired', label: 'Recompensas expiradas' },
	{ key: 'totalRewardsRefunded', label: 'Recompensas estornadas' },
	{ key: 'totalPartnerValue', label: 'Custo parceiro (R$)', format: formatBRL },
	{ key: 'totalFillins', label: 'Abastecimentos' },
	{ key: 'totalLiters', label: 'Litros abastecidos', format: formatLiters },
	{ key: 'totalFuelValue', label: 'Valor abastecido (R$)', format: formatBRL },
	{ key: 'pixGenerated', label: 'Pix gerados' },
	{ key: 'pixPaid', label: 'Pix pagos' },
	{ key: 'pixExpired', label: 'Pix expirados' },
	{ key: 'pixRevenue', label: 'Receita Pix (R$)', format: formatBRL },
	{ key: 'totalGuesses', label: 'Palpites concedidos' },
	{ key: 'guessesMade', label: 'Palpites realizados' },
	{ key: 'guessesPending', label: 'Palpites pendentes' },
	{ key: 'guessesWinners', label: 'Palpites vencedores' },
	{ key: 'totalLuckyNumbers', label: 'Números da sorte concedidos' },
	{ key: 'luckyNumbersPending', label: 'Números aguardando sorteio' },
	{ key: 'luckyNumbersWinners', label: 'Números sorteados' },
	{ key: 'totalSpins', label: 'Giros concedidos' },
	{ key: 'spinsUsed', label: 'Giros realizados' },
	{ key: 'spinsPending', label: 'Giros pendentes' },
	{ key: 'spinsExpired', label: 'Giros expirados' },
	{ key: 'spinsWinners', label: 'Giros premiados' },
];

const SummaryTable = ({ totals }: { totals: any }) => {
	if (!totals) return null;

	const rows = summaryRows
		.filter(({ key }) => totals[key] !== undefined && totals[key] !== null)
		.map(({ key, label, format }) => ({
			id: key,
			label,
			value: format ? format(totals[key]) : formatNumber(totals[key]),
		}));

	if (rows.length === 0) return null;

	return (
		<Datagrid bulkActionButtons={false} data={rows} total={rows.length} sx={{ mb: 4 }}>
			<TextField source="label" label="Métrica" sortable={false} />
			<TextField source="value" label="Valor" textAlign="center" sortable={false} />
		</Datagrid>
	);
};

const renderCountOrDash = (value: any): string =>
	value === undefined || value === null ? '-' : formatNumber(value);

const renderBRLOrDash = (value: any): string =>
	value === undefined || value === null ? '-' : formatBRL(value);

const GiftClubList = () => {
	const { data, filterValues } = useListContext();

	if (!filterValues?.giftId) {
		return <Typography align="center" sx={{ py: 4 }}>Selecione uma regra para visualizar o relatório.</Typography>;
	}

	let rows: any[] = [];
	let summaryData: any = null;

	if (data) {
		const info = data[0];
		if (info) {
			rows = info.data || [];
			summaryData = info.summaryData;
		}
	}

	const byProduct: any[] = summaryData?.byProduct || [];
	const byRule: any[] = summaryData?.byRule || [];

	return (
		<>
			<Typography sx={titleSx}>Resumo</Typography>
			<SummaryTable totals={summaryData?.totals} />

			<Typography sx={titleSx}>Recompensas</Typography>
			<Datagrid bulkActionButtons={false} data={byProduct} total={byProduct.length} sx={{ mb: 4 }}>
				<TextField source="productName" label="Recompensa" sortable={false} />
				<FunctionField
					label="Tipo"
					sortable={false}
					render={(record: any) => translateProductType(record.productType as GiftProductType)}
				/>
				<FunctionField
					label="Emitidas"
					textAlign="center"
					sortable={false}
					render={(record: any) => formatNumber(record.issued)}
				/>
				<FunctionField
					label="Utilizadas"
					textAlign="center"
					sortable={false}
					render={(record: any) => formatNumber(record.used)}
				/>
				<FunctionField
					label="Pendentes"
					textAlign="center"
					sortable={false}
					render={(record: any) => formatNumber(record.pending)}
				/>
				<FunctionField
					label="Expiradas"
					textAlign="center"
					sortable={false}
					render={(record: any) => formatNumber(record.expired)}
				/>
				<FunctionField
					label="Estornadas"
					textAlign="center"
					sortable={false}
					render={(record: any) => formatNumber(record.refunded)}
				/>
				<FunctionField
					label="Custo parceiro"
					textAlign="center"
					sortable={false}
					render={(record: any) => formatBRL(record.partnerValue)}
				/>
			</Datagrid>

			<Typography sx={titleSx}>Regras</Typography>
			<Datagrid bulkActionButtons={false} data={byRule} total={byRule.length} sx={{ mb: 4 }}>
				<FunctionField
					label="Regra"
					sortable={false}
					render={(record: any) => {
						const typeName = GiftRuleType.translateType(record.ruleType as GiftRuleType);
						const valueLabel = formatRuleValue(record.ruleValue, record.ruleValueType as GiftValueType);
						return `${typeName}${valueLabel}`;
					}}
				/>
				<TextField source="code" label="Código" sortable={false} emptyText="-" />
				<FunctionField
					label="Conquistas"
					textAlign="center"
					sortable={false}
					render={(record: any) => renderCountOrDash(record.achievements)}
				/>
				<FunctionField
					label="Clientes"
					textAlign="center"
					sortable={false}
					render={(record: any) => renderCountOrDash(record.customers)}
				/>
				<FunctionField
					label="Abastecimentos"
					textAlign="center"
					sortable={false}
					render={(record: any) => renderCountOrDash(record.fillins)}
				/>
				<FunctionField
					label="Litros"
					textAlign="center"
					sortable={false}
					render={(record: any) => renderCountOrDash(record.liters)}
				/>
				<FunctionField
					label="Valor abastecido"
					textAlign="center"
					sortable={false}
					render={(record: any) => renderBRLOrDash(record.fuelValue)}
				/>
				<FunctionField
					label="Pix gerados"
					textAlign="center"
					sortable={false}
					render={(record: any) => renderCountOrDash(record.pixGenerated)}
				/>
				<FunctionField
					label="Pix pagos"
					textAlign="center"
					sortable={false}
					render={(record: any) => renderCountOrDash(record.pixPaid)}
				/>
				<FunctionField
					label="Receita Pix"
					textAlign="center"
					sortable={false}
					render={(record: any) => renderBRLOrDash(record.pixRevenue)}
				/>
			</Datagrid>

			<Typography sx={titleSx}>Recompensas entregues</Typography>
			<Datagrid bulkActionButtons={false} data={rows} rowClick={false}>
				<ReferenceField
					label="Cliente"
					source="customerId"
					reference="customers"
					link="show"
					sortable={false}
				>
					<TextField source="name" />
				</ReferenceField>
				<TextField source="productName" label="Recompensa" sortable={false} />
				<FunctionField
					label="Tipo"
					sortable={false}
					render={(record: any) => translateProductType(record.productType as GiftProductType)}
				/>
				<FunctionField
					label="Status"
					sortable={false}
					render={(record: any) => <StatusChip record={record} />}
				/>
				<TextField source="code" label="Código" sortable={false} emptyText="-" />
				<TextField source="placeName" label="Posto" sortable={false} emptyText="-" />
				<DateField
					source="createdAt"
					label="Conquista"
					showTime
					locales="pt-BR"
					sortable={false}
				/>
				<DateField
					source="usedAt"
					label="Utilização"
					showTime
					locales="pt-BR"
					sortable={false}
					emptyText="-"
				/>
			</Datagrid>
		</>
	);
};

const FilterTablePrint = () => {
	const { filterValues } = useListContext();
	const { permissions } = usePermissions();
	return (
		<Datagrid bulkActionButtons={false} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
			<DateField source="from" textAlign="center" label="De" sortable={false} showTime locales="pt-BR" />
			<DateField source="to" textAlign="center" label="Até" sortable={false} showTime locales="pt-BR" />
			{
				permissions && permissions.includes(PERMISSIONS.ADMIN) &&
				<ReferenceField label="Rede" emptyText="Todas" source="chainId" textAlign="center" reference="chains" link={false} sortable={false}>
					<TextField source="name" />
				</ReferenceField>
			}
			<ReferenceField label="Regra" emptyText="-" source="giftId" reference="gifts" link={false} sortable={false}>
				<TextField source="name" />
			</ReferenceField>
			{
				filterValues.placeIds &&
				<ReferenceArrayField label="Postos" emptyText="Todos" source="placeIds" textAlign="center" reference="places">
					<SingleFieldList>
						<ChipField source="name" />
					</SingleFieldList>
				</ReferenceArrayField>
			}
			<DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
		</Datagrid>
	);
};

const ListFilters = (props: any) => {
	const { filterValues } = useListContext();
	return (
		<Filter {...props}>
			<BetweenDatesTimesInput format="YYYY-MM-DD HH:mm" alwaysOn />
			<Permission permission={PERMISSIONS.ADMIN} alwaysOn>
				<ReferenceInput
					reference="chains"
					source="chainId"
					perPage={null}
					sort={{ field: 'name', order: 'ASC' }}
				>
					<AutocompleteInput
						label="Rede"
						optionText="name"
						filterToQuery={filterToQuery}
					/>
				</ReferenceInput>
			</Permission>
			<ReferenceInput
				source="giftId"
				reference="gifts"
				filter={{ chainId: filterValues?.chainId || getChainId() }}
				sort={{ field: 'name', order: 'ASC' }}
				perPage={null}
				alwaysOn
			>
				<AutocompleteInput
					label="Regra"
					optionText="name"
					filterToQuery={filterToQuery}
				/>
			</ReferenceInput>
			<ReferenceArrayInput
				source="placeIds"
				reference="places"
				filter={{ chainId: filterValues?.chainId || getChainId() }}
				sort={{ field: 'name', order: 'ASC' }}
				perPage={null}
				alwaysOn
			>
				<AutocompleteArrayInput
					label="Postos"
					optionText="name"
					filterToQuery={filterToQuery}
				/>
			</ReferenceArrayInput>
		</Filter>
	);
};

const giftClubExporter = async (data: any[], fetchRelatedRecords: any) => {
	const rewardsInfo = data[0]?.data || [];

	const relations = [
		{ field: 'customerId', resource: 'customers' },
	];

	const fetchData = await fetchRelatedRecords(relations, rewardsInfo);
	const getRelated = (resource: string) =>
		fetchData.find(({ resource: res }: any) => res === resource)?.results || [];

	return rewardsInfo.map((item: any) => {
		const customer = getRelated('customers').find(({ id }: any) => id === item.customerId);
		return {
			cliente: customer?.name || 'Não identificado',
			recompensa: item.productName || '-',
			tipo: translateProductType(item.productType as GiftProductType),
			status: translateStatus(item.status, item.kind),
			codigo: item.code || '-',
			posto: item.placeName || '-',
			data_conquista: item.createdAt,
			data_utilizacao: item.usedAt || '-',
		};
	});
};

const ReportGiftClub = (props: any) => {
	return (
		<Permission permission={PERMISSIONS.GIFT}>
			<RequiredChain>
				<ReportList periodRequired
					{...props}
					basePath="gifts"
					title="Clube de Vantagens"
					resource="chains/chainId/reports/gift-club"
					filters={<ListFilters />}
					sort={{ field: 'createdAt', order: 'DESC' }}
					filterDefaultValues={{
						from: moment().startOf('month').toISOString(),
						to: moment().endOf('day').toISOString(),
					}}
					customExporter={giftClubExporter}
					bulkActionButtons={false}
					titleOnPrint="Relatório do Clube de Vantagens"
					filterTablePrint={<FilterTablePrint />}
					landscape
				>
					<GiftClubList />
				</ReportList>
			</RequiredChain>
		</Permission>
	);
};

export default ReportGiftClub;
